import { Injectable, signal, computed, InjectionToken, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, from, switchMap, map, tap, EMPTY, catchError } from 'rxjs';
import { User, AuthResponse, ApiResponse } from '../models';
import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  Auth,
  UserCredential,
} from 'firebase/auth';

export const FIREBASE_CONFIG = new InjectionToken<Record<string, string>>('FIREBASE_CONFIG');
export const API_URL = new InjectionToken<string>('API_URL');

@Injectable({ providedIn: 'root' })
export class AuthService {
  private currentUserSignal = signal<User | null>(this.getStoredUser());
  private firebaseAuth: Auth;
  private redirectPromise: Promise<UserCredential | null> | null = null;
  private apiUrl = inject(API_URL);

  currentUser = this.currentUserSignal.asReadonly();
  isAuthenticated = computed(() => !!this.currentUserSignal());

  constructor(private http: HttpClient, private router: Router) {
    const config = inject(FIREBASE_CONFIG);
    if (!getApps().length) {
      initializeApp(config);
    }
    this.firebaseAuth = getAuth();
  }

  private getStoredUser(): User | null {
    const user = localStorage.getItem('user');
    if (!user || user === 'undefined') return null;
    try {
      return JSON.parse(user);
    } catch {
      return null;
    }
  }

  private storeSession(data: AuthResponse): void {
    localStorage.setItem('token', data.token);
    localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('user', JSON.stringify(data.user));
    this.currentUserSignal.set(data.user);
  }

  signInWithGoogle(): void {
    signInWithRedirect(this.firebaseAuth, new GoogleAuthProvider());
  }

  handleRedirectResult(): Observable<void> {
    if (!this.redirectPromise) {
      this.redirectPromise = getRedirectResult(this.firebaseAuth);
    }
    return from(this.redirectPromise).pipe(
      switchMap((result) => {
        if (!result) {
          if (this.isAuthenticated()) {
            this.router.navigate(['/dashboard']);
          }
          return EMPTY;
        }
        return from(result.user.getIdToken()).pipe(
          switchMap((idToken: string) =>
            this.http.post<ApiResponse<AuthResponse>>(
              `${this.apiUrl}/auth/google`,
              { idToken }
            )
          ),
          tap((r) => {
            this.storeSession(r.data);
            this.router.navigate(['/dashboard']);
          }),
          map(() => undefined)
        );
      }),
      catchError((err) => {
        console.error('Auth redirect handling failed:', err);
        return EMPTY;
      })
    );
  }

  logout(): void {
    signOut(this.firebaseAuth).catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    this.currentUserSignal.set(null);
    this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }
}
